/**
 * "Does the canvas PAINT this point of the hatch?" — the one truth behind the
 * pointer pick (`raycast/distance.ts`) and crossing select
 * (`raycast/windowSelect.ts`).
 *
 * # Why it exists (@codex PR #785 round 17, P2)
 *
 * Round 16 (`aa21c37`) gave the HATCH its `$FILLMODE` reading: at an explicit
 * `$FILLMODE` 0 `scene/sceneMeshFactories.ts::buildHatchFillMesh` emits NO fill
 * of any tier and `batchedLayer.ts::hatchOutlineLineVertices` strokes the
 * boundary loops instead, so the interior is EMPTY SCREEN. The pointer pick was
 * not part of that commit: `distance.ts`'s hatch arm still answered 0 anywhere
 * inside the filled region, so a click deep inside a large OUTLINED hatch
 * selected geometry that is not drawn there — the pick a SUPERSET of the paint.
 *
 * That is the third turn of one defect. The WIDE POLYLINE band has read
 * {@link fillModePaintsSolid} in `distanceToDrawnPolyline` and in
 * `crossingReachesBandFill` since rounds 11/12, through the SAME predicate and
 * the SAME `RenderableEntity.fillmode` channel this module reads; the hatch arm
 * of both files was simply never joined to it.
 *
 * # Why ONE function and not the gate written twice
 *
 * Round 12's F1 was exactly a second consumer of one shape drifting from the
 * first (the pointer picked a band's interior at 0 while crossing select stayed
 * edge-only). The roles-plus-region assembly for a BAND therefore lives in one
 * place (`adapters/geom/polylineDrawnBand.ts::drawnBandFilledRegion`) and both
 * call it; this module is that same arrangement for a HATCH. A second
 * point-in-hatch test at either call site would be a parallel truth
 * (CLAUDE.md ПРИОРИТЕТ #2.1) and a `dup:check` clone besides.
 *
 * # The tagged-degenerate signal
 *
 * A TAGGED exterior that DEGENERATED to <3 vertices is DROPPED by the hatch
 * walk, so it is NOT in `geom.loops` and the tagged bit cannot be re-derived
 * from the loop set. `SceneGeometry.hadTaggedExterior` carries the RAW-source
 * `pathTypeFlag` bit (computed pre-drop in `buildHatchSceneGeometry`); a
 * synthetic 0-vertex exterior is injected so `isPointInFilledRegion`'s own
 * `loops.some(!isHole)` gate sees it and the hatch reads EMPTY instead of
 * promoting an interior loop to a fake fill (PR #410 P3). The injected loop is
 * filtered out by the predicate's own `vertices.length >= 3` pass, so it
 * contributes no area — it only carries the tag. This mirrors the drawing-model
 * pick (`hatchBoundaryGeometry.distanceToHatchBoundary`).
 */

import {
  isPointInFilledRegion,
  type FilledRegionLoop,
  type Point2D,
} from "@dxf-ai-editor/drawing-model";

import { fillModePaintsSolid } from "../adapters/geom/polylineDrawnBand";
import type { RenderableEntity, SceneGeometry, Vec3 } from "../types";

type HatchSceneGeometry = Extract<SceneGeometry, { kind: "hatch" }>;

/**
 * The hatch's boundary loops as an EXTERIOR-MINUS-HOLES region, ready for
 * `drawing-model`'s one-truth {@link isPointInFilledRegion}.
 *
 * `project` maps a loop vertex into the space the probe point lives in — the
 * identity (omitted) for the pointer, whose position is a top-view world XY, or
 * `WindowRefinement.mapPoint` for a twisted viewport's crossing select, where
 * both the window corners and the loops must be compared in PAPER space. The
 * common case allocates no vertices (a `Vec3` IS a `Point2D` with a `z` the 2D
 * predicate ignores).
 */
function hatchFilledRegion(
  geom: HatchSceneGeometry,
  project?: ((p: Vec3) => Point2D) | null,
): FilledRegionLoop[] {
  const loops: FilledRegionLoop[] = geom.loops.map((l) => ({
    vertices: project ? l.vertices.map(project) : l.vertices,
    isHole: l.kind === "interior",
  }));
  // See the header — the tagged-degenerate signal, carried rather than
  // re-derived.
  if (geom.hadTaggedExterior && !loops.some((l) => !l.isHole)) {
    loops.unshift({ vertices: [], isHole: false });
  }
  return loops;
}

/**
 * `true` when the canvas PAINTS `p` — i.e. the drawing's `$FILLMODE` says the
 * hatch is filled at all AND `p` lies in the exterior-minus-holes region the
 * fill mesh covers. A non-hatch entity is never painted by this rule.
 *
 * At an explicit `$FILLMODE` 0 this is `false` EVERYWHERE, which is the whole
 * point: the interior is empty screen and only the stroked boundary remains,
 * so both selection paths fall through to their boundary-distance / crossed-edge
 * tests and the user can select exactly the ink that is there.
 */
export function pointInPaintedHatch(
  p: Point2D,
  entity: RenderableEntity,
  project?: ((p: Vec3) => Point2D) | null,
): boolean {
  if (entity.geometry.kind !== "hatch") return false;
  // The ONE reader of `RenderableEntity.fillmode` for a hatch pick — the same
  // predicate `buildHatchFillMesh`, `hatchOutlineLineVertices`, the draw-order
  // plan, the band pick, the band's crossing-fill test and the PLOT all call.
  if (!fillModePaintsSolid(entity.fillmode)) return false;
  return isPointInFilledRegion(p, hatchFilledRegion(entity.geometry, project));
}
